import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnDestroy,
} from '@angular/core';

import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatMenu } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, of, Subject } from 'rxjs';
import {
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Customer } from 'server/shared/models/customer';
import { Invitee } from 'server/shared/models/invitee';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { PoolerService } from 'src/app/services/pooler/pooler.service';
import { UserService } from 'src/app/services/user.service';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { getJobCustomerTariff } from '../../../../helpers/getCustomerTariff';
import { getJobRestWarning } from '../../../../helpers/getRestWarning';
import { PermissionsService } from '../../../services/permissions.service';
import { TranslationService } from '../../../services/translation.service';
import { getJobInviteeStatusAndIcon } from '../../getInviteeStatusAndIcon';
import { HelperService } from '../../helper.service';
import { UserLogsBottomSheetComponent } from './user-logs-bottom-sheet/user-logs-bottom-sheet.component';

let isEmpty = require('lodash/isEmpty');

@Component({
  selector: 'app-job-invitee-list-item',
  templateUrl: 'job-invitee-list-item.component.html',
  styleUrls: ['job-invitee-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobInviteeListItemComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();
  private _bemPrefix: string = 'user-list-item';
  roleEnum = RoleEnum;

  invitee$ = new BehaviorSubject<Invitee | undefined>(undefined);
  @Input() set invitee(value: Invitee | undefined) {
    this.invitee$.next(value);
  }
  get invitee() {
    return this.invitee$.value;
  }

  count$ = new BehaviorSubject<number | undefined>(undefined);
  @Input() set count(value: number | undefined) {
    this.count$.next(value);
  }
  get count() {
    return this.count$.value;
  }

  totalCount$ = new BehaviorSubject<number | undefined>(undefined);
  @Input() set totalCount(value: number | undefined) {
    this.totalCount$.next(value);
  }
  get totalCount() {
    return this.totalCount$.value;
  }

  dropdownMenu$ = new BehaviorSubject<MatMenu | undefined>(undefined);
  @Input() set dropdownMenu(value: MatMenu | undefined) {
    this.dropdownMenu$.next(value);
  }
  get dropdownMenu() {
    return this.dropdownMenu$.value;
  }
  jobInput$ = new BehaviorSubject<Job | undefined>(undefined);
  job$ = this.jobInput$.pipe(rxFilterIsNotEmpty());
  @Input() set job(value: Job | undefined) {
    this.jobInput$.next(value);
  }
  get job() {
    return this.jobInput$.value;
  }

  loggedInCustomer$ = new BehaviorSubject<Customer | undefined>(undefined);
  @Input() set loggedInCustomer(value: Customer | undefined) {
    this.loggedInCustomer$.next(value);
  }
  get loggedInCustomer() {
    return this.loggedInCustomer$.value;
  }

  inviteeUser$ = this.invitee$.pipe(
    startWith(undefined),
    map((invitee) => invitee?.userId),
    switchMap((userId) => {
      if (!userId) return of(undefined);
      return this.poolerService.poolerUsersById$.pipe(
        map((poolerUsersById) => poolerUsersById.get(userId)),
        rxDistinctJSONStringify(),
      );
    }),
    shareReplay(1),
  );

  isCanceled$ = new BehaviorSubject<boolean>(false);
  @HostBinding('class.is-canceled')
  get isCanceled() {
    return this.isCanceled$.value;
  }

  isCanceledEffect$ = this.invitee$.pipe(
    takeUntil(this.destroyed$),
    map((invitee) => invitee?.status === InviteeStatusEnum.CANCELED),
    tap((isCanceled) => this.isCanceled$.next(isCanceled)),
    // uses behaviorsubject & CDR because of Hostbinding
    tap(() => this.cdr.markForCheck()),
  );

  restData$ = combineLatest([this.inviteeUser$, this.job$]).pipe(
    map(([user, job]) => {
      if (!user) return;
      return getJobRestWarning(
        {
          date: job.date,
          shifts: job.shifts,
          jobId: job._id,
          invitees: job.invitees,
        },
        user,
        this.i18n,
      );
    }),
    shareReplay(1),
  );

  restStatus$ = this.restData$.pipe(map((data) => data?.restStatus));
  restWarning$ = this.restData$.pipe(map((data) => data?.restWarning));

  availabilityNote$ = this.inviteeUser$.pipe(
    map((user) => {
      if (!user) return;
      const availability = user.availabilities?.find(
        (availability) => availability.date === this.job?.date,
      );
      return availability?.note;
    }),
  );
  inviteeStatusEnum = InviteeStatusEnum;
  jobStatusEnum = JobStatusEnum;
  showDetails$ = new BehaviorSubject<boolean>(false);
  animationShowDetails$ = new BehaviorSubject<boolean>(false);
  sexEnum = SexEnum;

  i18n = this.translationService.getTranslations('jobs');

  requestState$ = this.invitee$.pipe(
    map((invitee) => {
      if (!invitee) return;
      const { requestState } = getJobInviteeStatusAndIcon(invitee);
      return requestState;
    }),
  );

  componentClasses$ = this.requestState$.pipe(
    map((requestState) => {
      let tmp = [this._bemPrefix];
      if (!isEmpty(requestState) && requestState) {
        tmp.push(this.buildModifierClass(requestState));
      }
      return tmp.join(' ');
    }),
    shareReplay(1),
  );

  inviteeHasTimeTracking$ = this.invitee$.pipe(
    map((invitee) => {
      return (
        invitee?.status === InviteeStatusEnum.BOOKED &&
        invitee?.timeTrackings &&
        !invitee?.timeTrackings.some((t) => t.disabled)
      );
    }),
  );

  readyDispo$ = this.userService.readyDispo$;

  isSharedPooler$ = combineLatest([this.invitee$, this.loggedInCustomer$]).pipe(
    map(
      ([invitee, loggedInCustomer]) =>
        invitee?.customer.id !== loggedInCustomer?._id,
    ),
    shareReplay(1),
  );

  sourceCustomer$ = combineLatest([
    this.invitee$.pipe(rxFilterIsNotEmpty()),
    this.inviteeUser$,
    this.loggedInCustomer$,
  ]).pipe(
    map(([invitee, inviteeUser, loggedInCustomer]) => {
      const certificates =
        invitee?.personalData?.certificates || inviteeUser?.certificates || [];

      return {
        id: invitee?.customer.id,
        displayName: invitee.customer.displayName,
        tariff: loggedInCustomer
          ? getJobCustomerTariff(
              loggedInCustomer,
              invitee.customer.id,
              certificates,
            )
          : '',
      };
    }),
    shareReplay(1),
  );

  displayInvitee$ = combineLatest([
    this.invitee$,
    this.isSharedPooler$,
    this.restStatus$,
  ]).pipe(
    map(([invitee, isSharedPooler, restStatus]) => {
      return (
        invitee &&
        (!isSharedPooler ||
          !restStatus ||
          restStatus.hasRested ||
          invitee?.status === InviteeStatusEnum.BOOKED ||
          invitee?.status === InviteeStatusEnum.POSSIBLE_CANDIDATE ||
          invitee?.status === InviteeStatusEnum.WORKING)
      );
    }),
  );

  hasOpenHoursToApprove$ = this.invitee$.pipe(
    map(
      (invitee) =>
        !!invitee?.timeTrackings?.some(
          (timeTracking) =>
            timeTracking.toBeApprovedAt && !timeTracking.approvedAt,
        ),
    ),
  );

  hasHoursToRecord$ = this.invitee$.pipe(
    map(
      (invitee) =>
        !!invitee?.timeTrackings?.some(
          (timeTracking) =>
            !timeTracking.toBeApprovedAt &&
            !timeTracking.approvedAt &&
            !timeTracking.disabled,
        ),
    ),
  );

  isDropdownDisabled$ = combineLatest([
    this.job$,
    this.readyDispo$,
    this.loggedInCustomer$,
    this.invitee$,
    this.permissionsService.completePermissions$,
  ]).pipe(
    map(([job, loggedInUser, loggedInCustomer, invitee, dispoPermissions]) => {
      if (!job || !loggedInUser || !loggedInCustomer) return true;
      const userCanEditJob = this.permissionsService.userCanEditJob(
        job,
        dispoPermissions,
      );
      if (!userCanEditJob) {
        return true;
      }
      const customerCanEditJob = this.permissionsService.customerCanEditJob(
        loggedInCustomer,
        job,
      );
      if (!customerCanEditJob) {
        return true;
      }
      if (job && invitee && loggedInUser) {
        return (
          invitee.status == InviteeStatusEnum.WORKING ||
          invitee.status == InviteeStatusEnum.TRANSFERRED ||
          (invitee.status != InviteeStatusEnum.BOOKED &&
            job.status != JobStatusEnum.OPEN)
        );
      } else {
        return true;
      }
    }),
  );

  constructor(
    private bottomSheet: MatBottomSheet,
    private translationService: TranslationService,
    public permissionsService: PermissionsService,
    private route: ActivatedRoute,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private poolerService: PoolerService,
  ) {
    this.isCanceledEffect$.subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public openBottomSheet(): void {
    this.readyDispo$.pipe(take(1)).subscribe((dispo) => {
      const invitee = this.job?.invitees.find(
        (invitee) => invitee.id == this.invitee?.id,
      );
      this.route.params.subscribe(async (params) => {
        this.bottomSheet.dismiss();
      });
      this.bottomSheet.open(UserLogsBottomSheetComponent, {
        data: {
          customerId: dispo?.customerId,
          invitee: invitee,
          jobId: this.job?._id,
          cancelReasonDescription: this.job?.cancelReasonDescription,
        },
      });
    });
  }

  private buildModifierClass(modifier: string): string {
    return HelperService.buildBemModifierClass(this._bemPrefix, modifier);
  }

  toggleDetails(event: Event) {
    event.preventDefault();
    if (this.showDetails$.value) {
      this.animationShowDetails$.next(false);
      // un-render elements after animation delay
      setTimeout(() => {
        this.showDetails$.next(false);
      }, 1000);
    } else {
      this.showDetails$.next(true);
      // start animation immediately after rendering elements
      setTimeout(() => {
        this.animationShowDetails$.next(true);
      });
    }
  }
}
